/**
 * 메뉴 관리 페이지 (관리자 설정 탭)
 *
 * DDB agentBuilder_menus / menuLang / menuAuth 관리
 */

import { useState, useEffect } from 'react';
import { Button, Badge, Checkbox, Modal, Select, SearchInput, SortableTh, useTableSort, useToast } from '../components/ui';
import { PanelTitle, Topbar } from '../components/layout';
import { menuApi, MenuItem } from '../api/menus';
import { roleApi, Role } from '../api/roles';
import EditIcon from '../assets/icons/common/edit.svg?react';
import TrashIcon from '../assets/icons/common/trash.svg?react';
import '../styles/adminPage.css';

// 권한 체크박스 컬럼 - 동적으로 roles 테이블에서 로드

interface MenuRow extends MenuItem {
  langKo: string;
  langEn: string;
}

const EMPTY_MENU: Partial<MenuItem> = {
  menuIdx: 0,
  menuCd: '',
  icon: '',
  menuUrl: '',
  depth: '0',
  parentIdx: 0,
  section: 'main',
  sortOrder: 1,
  useYn: 'Y',
  menuYn: 'Y',
  popupYn: 'N',
};

export function MenuManagementPage({ embedded = false }: { embedded?: boolean }) {
  const { showToast } = useToast();
  const [rows, setRows] = useState<MenuRow[]>([]);
  const [roles, setRoles] = useState<Role[]>([]); // 동적 권한 목록
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState(''); // 메뉴명(ko/en) · menuCd · URL 검색
  // 정렬 - 칸을 고르기 전엔 트리 순서(부모 → 자식), 고르면 그 칸 기준 평면 정렬
  const sort = useTableSort<'menuCd' | 'langKo' | 'menuUrl' | 'depth' | 'useYn'>();
  // 역할별 접근 권한 맵 { productAdmin: [menuIdx...], tenantAdmin: [...], ... }
  const [authByRole, setAuthByRole] = useState<Record<string, number[]>>({});
  const [savingAuth, setSavingAuth] = useState(false);

  // 편집 모달
  const [editRow, setEditRow] = useState<MenuRow | null>(null);
  const [editForm, setEditForm] = useState<{
    menu: Partial<MenuItem>;
    langKo: string;
    langEn: string;
  }>({ menu: { ...EMPTY_MENU }, langKo: '', langEn: '' });
  const [saving, setSaving] = useState(false);
  // 필수 입력 에러 (필드명 집합) — 토스트 대신 input 빨간 하이라이트
  const [formErrors, setFormErrors] = useState<Set<string>>(new Set());
  const clearFormError = (field: string) => {
    setFormErrors((prev) => {
      if (!prev.has(field)) return prev;
      const next = new Set(prev);
      next.delete(field);
      return next;
    });
  };
  const [deletingIdx, setDeletingIdx] = useState<number | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<MenuRow | null>(null);

  useEffect(() => { load(); loadRoles(); }, []);

  const loadRoles = async () => {
    try {
      const data = await roleApi.list();
      setRoles(data);
    } catch {
      // 권한 목록 로드 실패 시 기본 권한 사용
      setRoles([
        { roleId: 'productAdmin', roleName: '제품 관리자', createdAt: '', updatedAt: '' },
        { roleId: 'tenantAdmin', roleName: '테넌트 관리자', createdAt: '', updatedAt: '' },
        { roleId: 'customerAdmin', roleName: '고객사 관리자', createdAt: '', updatedAt: '' },
        { roleId: 'user', roleName: '사용자', createdAt: '', updatedAt: '' },
      ]);
    }
  };

  const load = async () => {
    try {
      setLoading(true);
      const data = await menuApi.getAdminAll();
      const langKoMap = new Map(data.langKo.map(l => [l.menuCd, l.menuNm]));
      const langEnMap = new Map(data.langEn.map(l => [l.menuCd, l.menuNm]));
      const built: MenuRow[] = (data.menus as MenuItem[]).map(m => ({
        ...m,
        menuNm: langKoMap.get(m.menuCd) ?? m.menuCd,
        langKo: langKoMap.get(m.menuCd) ?? '',
        langEn: langEnMap.get(m.menuCd) ?? '',
      }));
      setRows(built);
      // 역할별 권한 맵 (백엔드 authByRole 우선, 없으면 레거시 authAdmin/authUser로 폴백)
      const map: Record<string, number[]> = { ...(data.authByRole || {}) };
      if (!map.productAdmin && data.authAdmin) map.productAdmin = data.authAdmin;
      if (!map.user && data.authUser) map.user = data.authUser;
      setAuthByRole(map);
    } catch {
      showToast('error', '메뉴 데이터를 불러오는데 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const openAdd = () => {
    const maxIdx = rows.reduce((m, r) => Math.max(m, r.menuIdx), 0);
    setEditRow(null);
    setEditForm({ menu: { ...EMPTY_MENU, menuIdx: maxIdx + 1 }, langKo: '', langEn: '' });
  };

  const openEdit = (row: MenuRow) => {
    setEditRow(row);
    setEditForm({ menu: { ...row }, langKo: row.langKo, langEn: row.langEn });
  };

  const closeModal = () => { setEditRow(null); setEditForm({ menu: { ...EMPTY_MENU }, langKo: '', langEn: '' }); setFormErrors(new Set()); };

  const handleSave = async () => {
    const { menu, langKo, langEn } = editForm;
    // 필수 입력 검증 — 미입력 필드 빨간 하이라이트 (토스트 없음)
    const errs = new Set<string>();
    if (!menu.menuIdx) errs.add('menuIdx');
    if (!menu.menuCd?.trim()) errs.add('menuCd');
    if (errs.size > 0) { setFormErrors(errs); return; }
    setFormErrors(new Set());
    setSaving(true);
    try {
      await menuApi.saveMenu(
        menu,
        { menuCd: menu.menuCd!, langCd: 'ko', menuNm: langKo },
        { menuCd: menu.menuCd!, langCd: 'en', menuNm: langEn },
      );
      showToast('success', '저장되었습니다.');
      closeModal();
      load();
      window.dispatchEvent(new CustomEvent('menu-changed'));
    } catch { showToast('error', '저장 실패'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (row: MenuRow) => {
    setDeleteConfirm(row);
  };

  const confirmDelete = async () => {
    if (!deleteConfirm) return;
    setDeletingIdx(deleteConfirm.menuIdx);
    try {
      await menuApi.deleteMenu(deleteConfirm.menuIdx, deleteConfirm.menuCd);
      showToast('success', '삭제되었습니다.');
      load();
    } catch { showToast('error', '삭제 실패'); }
    finally {
      setDeletingIdx(null);
      setDeleteConfirm(null);
    }
  };

  const toggleAuth = (roleId: string, menuIdx: number) => {
    setAuthByRole(prev => {
      const list = prev[roleId] ?? [];
      const next = list.includes(menuIdx) ? list.filter(i => i !== menuIdx) : [...list, menuIdx];
      return { ...prev, [roleId]: next };
    });
  };

  const handleSaveAuth = async () => {
    setSavingAuth(true);
    try {
      await Promise.all(
        roles.map(role => menuApi.saveMenuAuth(role.roleId, authByRole[role.roleId] ?? []))
      );
      showToast('success', '권한이 저장되었습니다.');
      window.dispatchEvent(new CustomEvent('menu-changed'));
    } catch { showToast('error', '권한 저장 실패'); }
    finally { setSavingAuth(false); }
  };

  // depth=0 부모 목록 (parentIdx 선택용)
  const parentMenus = rows.filter(r => r.depth === '0');

  return (
    <>
      {!embedded && (
        <Topbar title="메뉴 관리" description="사이드바 메뉴 항목과 역할별 접근 권한을 관리합니다" />
      )}
      <div className={embedded ? '' : 'page-content'}>
      <div className="admin-panel-head">
        {embedded ? <PanelTitle title="메뉴 관리" description="사이드바 메뉴 항목과 역할별 접근 권한을 관리합니다." /> : <span />}
        <div className="admin-panel-actions">
          {/* 검색 (사용자 관리 헤더 검색과 같은 자리 · 크기) */}
          <div className="users-search-bar">
            <SearchInput
              placeholder="메뉴명, menuCd, URL로 검색..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onClear={() => setSearchQuery('')}
            />
          </div>
          <Button color="gray" appearance="outline" onClick={handleSaveAuth} disabled={savingAuth}>
            {savingAuth ? '저장 중...' : '권한 저장'}
          </Button>
          <Button color="primary" onClick={openAdd}>+ 메뉴 추가</Button>
        </div>
      </div>

      <div className="admin-section">
      {loading ? (
        <div className="loading">로딩 중...</div>
      ) : (
        <>
          {/* 메뉴 목록 테이블 */}
          <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th><SortableTh sort={sort} col="menuCd">menuCd</SortableTh></th>
                <th><SortableTh sort={sort} col="langKo">메뉴명</SortableTh></th>
                <th><SortableTh sort={sort} col="menuUrl">URL</SortableTh></th>
                <th><SortableTh sort={sort} col="depth">깊이</SortableTh></th>
                <th><SortableTh sort={sort} col="useYn">사용</SortableTh></th>
                {roles.map((role) => (
                  <th key={role.roleId}>{role.roleName}</th>
                ))}
                <th>작업</th>
              </tr>
            </thead>
            <tbody>
              {(() => {
                // 부모(depth=0)를 section+sortOrder로 정렬
                const parents = [...rows]
                  .filter(r => r.depth === '0')
                  .sort((a, b) => {
                    if (a.section !== b.section) return a.section === 'main' ? -1 : 1;
                    return a.sortOrder - b.sortOrder;
                  });
                // 자식(depth=1)을 parentIdx+sortOrder로 정렬
                const children = [...rows]
                  .filter(r => r.depth === '1')
                  .sort((a, b) => a.parentIdx !== b.parentIdx ? a.parentIdx - b.parentIdx : a.sortOrder - b.sortOrder);

                // 부모 뒤에 해당 자식 삽입
                const ordered: MenuRow[] = [];
                parents.forEach(parent => {
                  ordered.push(parent);
                  children.filter(c => c.parentIdx === parent.menuIdx).forEach(c => ordered.push(c));
                });
                // 부모가 없는 고아 자식도 마지막에 추가
                children.filter(c => !parents.find(p => p.menuIdx === c.parentIdx)).forEach(c => ordered.push(c));

                // 검색어가 있으면 메뉴명(ko/en) · menuCd · URL 에 포함된 행만 (트리 순서는 유지)
                const q = searchQuery.trim().toLowerCase();
                const visible = q
                  ? ordered.filter(r => [r.langKo, r.langEn, r.menuCd, r.menuUrl].some(v => (v || '').toLowerCase().includes(q)))
                  : ordered;
                return sort.sortRows(visible, (r, key) => r[key] || '').map(row => (
                <tr key={row.menuIdx} className={row.depth === '1' ? 'sub' : undefined}>
                  <td className="left mono">{row.menuCd}</td>
                  <td className={row.depth === '1' ? 'left menu-name-sub' : 'left'}>
                    {row.depth === '1' && <span className="menu-tree">└</span>}
                    {/* 메뉴명 클릭 = 수정(✎) 버튼과 같은 편집 모달 */}
                    <a href="#" className="table-link" onClick={(e) => { e.preventDefault(); openEdit(row); }}>
                      {row.langKo || row.menuCd}
                    </a>
                  </td>
                  <td className="left mono mute">{row.menuUrl}</td>
                  <td>
                    <Badge variant={row.depth === '0' ? 'primary' : 'gray'} shape="tag">{row.depth}</Badge>
                  </td>
                  <td>
                    <Badge color={row.useYn === 'Y' ? 'success' : 'gray'} shape="tag">{row.useYn}</Badge>
                  </td>
                  {roles.map((role) => (
                    <td key={role.roleId}>
                      <Checkbox
                        checked={(authByRole[role.roleId] ?? []).includes(row.menuIdx)}
                        onChange={() => toggleAuth(role.roleId, row.menuIdx)}
                        aria-label={`${role.roleName} 접근`}
                      />
                    </td>
                  ))}
                  <td>
                    <div className="user-actions">
                      <button className="icon-action-btn" onClick={() => openEdit(row)} title="수정">
                        <EditIcon />
                      </button>
                      <button className="icon-action-btn delete" onClick={() => handleDelete(row)} disabled={deletingIdx === row.menuIdx} title="삭제">
                        <TrashIcon />
                      </button>
                    </div>
                  </td>
                </tr>
                ));
              })()}
            </tbody>
          </table>
          </div>

          {/* 권한은 상단 버튼으로 저장 */}
        </>
      )}
      </div>

      {/* 메뉴 추가 / 편집 모달 */}
      <Modal
        isOpen={editForm.menu.menuIdx !== undefined && editForm.menu.menuIdx > 0}
        onClose={closeModal}
        title={editRow ? '메뉴 편집' : '메뉴 추가'}
        footerRight={
          <>
            <Button color="gray" appearance="outline" onClick={closeModal}>취소</Button>
            <Button color="primary" onClick={handleSave} disabled={saving}>
              {saving ? '저장 중...' : '저장'}
            </Button>
          </>
        }
      >
        <div className="form-grid form-grid--2 menu-form">
          {/* menuIdx / menuCd */}
          <div className="form-item">
            <div className="form-label">menuIdx <span className="required">*</span></div>
            <div className={`input${formErrors.has('menuIdx') ? ' has-error' : ''}`}>
              <input className="input-control" type="number"
                value={editForm.menu.menuIdx ?? ''}
                onChange={e => { setEditForm(f => ({ ...f, menu: { ...f.menu, menuIdx: Number(e.target.value) } })); clearFormError('menuIdx'); }}
                disabled={!!editRow}
              />
            </div>
          </div>
          <div className="form-item">
            <div className="form-label">menuCd <span className="required">*</span></div>
            <div className={`input${formErrors.has('menuCd') ? ' has-error' : ''}`}>
              <input className="input-control" type="text" placeholder="예: admin_menu"
                value={editForm.menu.menuCd ?? ''}
                onChange={e => { setEditForm(f => ({ ...f, menu: { ...f.menu, menuCd: e.target.value } })); clearFormError('menuCd'); }}
                disabled={!!editRow}
              />
            </div>
          </div>

          {/* 메뉴명 ko / en */}
          <div className="form-item">
            <div className="form-label">메뉴명 (ko)</div>
            <div className="input">
              <input className="input-control" type="text" placeholder="한국어 메뉴명"
                value={editForm.langKo}
                onChange={e => setEditForm(f => ({ ...f, langKo: e.target.value }))}
              />
            </div>
          </div>
          <div className="form-item">
            <div className="form-label">메뉴명 (en)</div>
            <div className="input">
              <input className="input-control" type="text" placeholder="English menu name"
                value={editForm.langEn}
                onChange={e => setEditForm(f => ({ ...f, langEn: e.target.value }))}
              />
            </div>
          </div>

          {/* URL / icon */}
          <div className="form-item">
            <div className="form-label">menuUrl</div>
            <div className="input">
              <input className="input-control" type="text" placeholder="/admin/settings"
                value={editForm.menu.menuUrl ?? ''}
                onChange={e => setEditForm(f => ({ ...f, menu: { ...f.menu, menuUrl: e.target.value } }))}
              />
            </div>
          </div>
          <div className="form-item">
            <div className="form-label">icon</div>
            <div className="input">
              <input className="input-control" type="text" placeholder="menuCd와 동일하게 (예: admin)"
                value={editForm.menu.icon ?? ''}
                onChange={e => setEditForm(f => ({ ...f, menu: { ...f.menu, icon: e.target.value } }))}
              />
            </div>
          </div>

          {/* depth / 부모 메뉴 */}
          <div className="form-item">
            <div className="form-label">depth</div>
            <Select
              value={editForm.menu.depth ?? '0'}
              onChange={e => setEditForm(f => ({
                ...f,
                menu: {
                  ...f.menu,
                  depth: e.target.value,
                  // 서브로 바꿀 때 부모가 비어 있으면 첫 번째 메인 메뉴를 기본 선택
                  parentIdx: e.target.value === '0' ? 0 : (f.menu.parentIdx || parentMenus[0]?.menuIdx || 0),
                },
              }))}
            >
              <option value="0">0 (메인)</option>
              <option value="1">1 (서브)</option>
            </Select>
          </div>
          <div className="form-item">
            <div className="form-label">부모 메뉴</div>
            <Select
              value={editForm.menu.parentIdx ?? 0}
              onChange={e => setEditForm(f => ({ ...f, menu: { ...f.menu, parentIdx: Number(e.target.value) } }))}
              disabled={editForm.menu.depth !== '1'}
            >
              {editForm.menu.depth !== '1' && <option value={0}>없음 (메인 메뉴)</option>}
              {parentMenus.map(p => (
                <option key={p.menuIdx} value={p.menuIdx}>{p.langKo || p.menuCd} (idx:{p.menuIdx})</option>
              ))}
            </Select>
          </div>

          {/* section / 정렬순서 */}
          <div className="form-item">
            <div className="form-label">section</div>
            <Select
              value={editForm.menu.section ?? 'main'}
              onChange={e => setEditForm(f => ({ ...f, menu: { ...f.menu, section: e.target.value } }))}
            >
              <option value="main">main</option>
              <option value="admin">admin</option>
            </Select>
          </div>
          <div className="form-item">
            <div className="form-label">정렬순서</div>
            <div className="input">
              <input className="input-control" type="number"
                value={editForm.menu.sortOrder ?? 1}
                onChange={e => setEditForm(f => ({ ...f, menu: { ...f.menu, sortOrder: Number(e.target.value) } }))}
              />
            </div>
          </div>

          {/* 사용여부 / menuYn */}
          <div className="form-item">
            <div className="form-label">사용여부</div>
            <Select
              value={editForm.menu.useYn ?? 'Y'}
              onChange={e => setEditForm(f => ({ ...f, menu: { ...f.menu, useYn: e.target.value } }))}
            >
              <option value="Y">Y</option>
              <option value="N">N</option>
            </Select>
          </div>
          <div className="form-item">
            <div className="form-label">menuYn</div>
            <Select
              value={editForm.menu.menuYn ?? 'Y'}
              onChange={e => setEditForm(f => ({ ...f, menu: { ...f.menu, menuYn: e.target.value } }))}
            >
              <option value="Y">Y</option>
              <option value="N">N</option>
            </Select>
          </div>
        </div>
      </Modal>

      {/* 메뉴 삭제 확인 모달 */}
      <Modal
        isOpen={!!deleteConfirm}
        onClose={() => setDeleteConfirm(null)}
        title="메뉴 삭제"
        size="sm"
        style={{ maxWidth: 360 }}
        bodyClassName="dialog-body--compact"
        footerRight={
          <>
            <Button color="gray" appearance="outline" onClick={() => setDeleteConfirm(null)}>취소</Button>
            <Button color="error" onClick={confirmDelete} disabled={deletingIdx !== null}>삭제</Button>
          </>
        }
      >
        <p style={{ margin: '4px 0 12px', lineHeight: 1.6, textAlign: 'center' }}>
          "{deleteConfirm?.langKo || deleteConfirm?.menuCd}" 메뉴를 삭제하시겠습니까?<br />삭제 후 복구할 수 없습니다.
        </p>
      </Modal>
      </div>
    </>
  );
}

export default MenuManagementPage;
